import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { caisseAPI, userAPI } from '../../services/api';
import Filter from '../../components/ui/Filter';

const WithdrawalRequestFilters = ({
  filters,
  onFiltersChange,
  onReset,
  userRole = 'MANAGER' // Default to MANAGER for admin view
}) => {
  const [caisses, setCaisses] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [managerUsers, setManagerUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      setLoading(true);
      setError(null);
      try {
        if (userRole === 'MANAGER') {
          // MANAGER users need caisses, assignable users (agents + managers), and managers only
          const [caissesRes, assignableRes, managersRes] = await Promise.all([
            caisseAPI.getForFilters(),
            userAPI.getAssignableUsers(), // All users (agents + managers)
            userAPI.getManagerUsers() // Managers only
          ]);
          
          setCaisses(Array.isArray(caissesRes.data) ? caissesRes.data : []);
          setAssignableUsers(Array.isArray(assignableRes.data) ? assignableRes.data : []);
          setManagerUsers(Array.isArray(managersRes.data) ? managersRes.data : []);
        } else {
          // AGENT users only need managers for "Created by" filter
          const managersRes = await userAPI.getManagerUsers();
          setManagerUsers(Array.isArray(managersRes.data) ? managersRes.data : []);
        }
      } catch (error) {
        console.error('Error loading filter options:', error);
        setError('Erreur lors du chargement des filtres');
      } finally {
        setLoading(false);
      }
    };

    loadFilterOptions();
  }, [userRole]);

  // Handle individual filter changes
  const handleFilterChange = (filterName, value) => {
    let updatedFilters = {
      ...filters,
      [filterName]: value
    };

    onFiltersChange(updatedFilters);
  };

  // Define filter configuration based on user role
  const getFilterOptions = () => {
    const baseFilters = [
      {
        name: 'searchTerm',
        type: 'search',
        label: 'Rechercher',
        placeholder: 'Recherche...',
        isMultiSelect: false
      },
      {
        name: 'statusIds',
        type: 'dropdown',
        label: 'Statut',
        placeholder: 'Statut: Tous',
        isMultiSelect: true,
        options: [
          { value: 'OPEN', label: 'Ouvert' },
          { value: 'CONFIRMED', label: 'Confirmé' },
          { value: 'VALIDATED', label: 'Validé' },
          { value: 'CANCELLED', label: 'Annulé' }
        ]
      }
    ];

    // Role-specific filters
    if (userRole === 'MANAGER') {
      baseFilters.push(
        {
          name: 'caisseIds',
          type: 'dropdown',
          label: 'Caisse',
          placeholder: 'Caisse: Tous',
          isMultiSelect: true,
          options: Array.isArray(caisses) ? caisses.map(caisse => ({
            id: caisse.id,
            value: caisse.id,
            label: caisse.name
          })) : []
        },
        {
          name: 'assignedToIds',
          type: 'dropdown',
          label: 'Assigné à',
          placeholder: 'Assigné à: Tous',
          isMultiSelect: true,
          options: Array.isArray(assignableUsers) ? assignableUsers.map(user => ({
            id: user.id,
            value: user.id,
            label: `${user.firstName} ${user.lastName}`
          })) : []
        }
      );
    }

    // Common filters for all roles
    baseFilters.push(
      {
        name: 'createdByIds',
        type: 'dropdown',
        label: 'Créé par',
        placeholder: 'Créé par: Tous',
        isMultiSelect: true,
        options: Array.isArray(managerUsers) ? managerUsers.map(user => ({
          id: user.id,
          value: user.id,
          label: `${user.firstName} ${user.lastName}`
        })) : []
      },
      {
        name: 'dateRange',
        type: 'daterange',
        label: 'Date',
        placeholder: 'Date',
        isMultiSelect: false
      }
    );

    return baseFilters;
  };

  const filterOptions = getFilterOptions();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 0 }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Component */}
      <Filter
        filters={filterOptions}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        onReset={onReset}
        showApplyButton={false}
      />
    </Box>
  );
};

export default WithdrawalRequestFilters;
